import { getIncidentTypeModuleLabels } from "./matter-type-module-labels";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { AddOrEditIncidentTypeModal } from "@/components/features/settings/defaults/matter-types/AddOrEditMatterTypeModal";
import { IncidentTypeContextMenu } from "@/components/features/settings/defaults/matter-types/MatterTypeContextMenu";
import { useUser } from "@/components/UserProvider";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import {
  ActionConfirmationModal,
  Button,
  Badge,
  Card,
  CardContent,
  Text,
} from "@/components/ui";
import {
  retrieveIncidentTypeCategoriesQueryOptions,
  useDeleteIncidentType,
} from "@/lib/data/react-query";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type IncidentType } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { Briefcase, ChevronRight, Pencil, Plus } from "lucide-react";
import { useMemo, useState } from "react";

const MODULE_CHIP_CLASS =
  "border-border/70 bg-muted/55 text-foreground/85 font-normal shadow-none";

const VISIBLE_MODULE_CHIPS = 5;

const CATEGORY_ACCENTS: Record<
  string,
  { iconClass: string; boxClass: string }
> = {
  "Personal Injury": {
    iconClass: "text-blue-600 dark:text-blue-300",
    boxClass:
      "border-blue-100 bg-blue-50 dark:border-blue-400/20 dark:bg-blue-400/10",
  },
  "Medical Malpractice": {
    iconClass: "text-purple-600 dark:text-purple-300",
    boxClass:
      "border-purple-100 bg-purple-50 dark:border-purple-400/20 dark:bg-purple-400/10",
  },
  "Wrongful Death": {
    iconClass: "text-muted-foreground",
    boxClass: "border-border bg-muted/50",
  },
};

const DEFAULT_CATEGORY_ACCENT = {
  iconClass: "text-muted-foreground",
  boxClass: "border-border bg-muted/50",
};

function IncidentTypeModuleChips({
  incidentType,
}: {
  incidentType: IncidentType;
}) {
  const modules = getIncidentTypeModuleLabels(incidentType);
  if (modules.length === 0) {
    return null;
  }
  const visible = modules.slice(0, VISIBLE_MODULE_CHIPS);
  const rest = modules.slice(VISIBLE_MODULE_CHIPS);
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {visible.map((label) => (
        <Badge
          key={label}
          variant="outline"
          className={cn("text-xs", MODULE_CHIP_CLASS)}
        >
          {label}
        </Badge>
      ))}
      {rest.length > 0 ? (
        <TooltipWrapper message={rest.join("\n")}>
          <Badge variant="outline" className={cn("text-xs", MODULE_CHIP_CLASS)}>
            +{rest.length} more
          </Badge>
        </TooltipWrapper>
      ) : null}
    </div>
  );
}

function IncidentTypesSettingsViewInner({
  title = "Incident types",
  addButtonLabel = "Add incident type",
}: {
  title?: string;
  addButtonLabel?: string;
}) {
  const [isAddingType, setIsAddingType] = useState(false);
  const [addingTypeCategoryId, setAddingTypeCategoryId] = useState<
    string | undefined
  >();
  const [isEditingType, setIsEditingType] = useState(false);
  const [activeTypeId, setActiveTypeId] = useState<string | null>(null);
  const [incidentTypeToDeleteId, setIncidentTypeToDeleteId] = useState<
    string | null
  >(null);
  const deleteIncidentType = useDeleteIncidentType();
  const { hasActionPermission } = useUser();

  const {
    data: matterSettingsData,
    isLoading,
    isError,
  } = useQuery(retrieveMatterSettingsQueryOptions());
  const {
    data: incidentTypeCategoriesData,
    isLoading: isIncidentTypeCategoriesLoading,
    isError: isIncidentTypeCategoriesError,
  } = useQuery(retrieveIncidentTypeCategoriesQueryOptions());

  const incidentTypes = useMemo(
    () => matterSettingsData?.data.incidentTypes ?? [],
    [matterSettingsData],
  );

  const incidentTypeCategories = useMemo(
    () => incidentTypeCategoriesData?.data ?? [],
    [incidentTypeCategoriesData?.data],
  );
  const displayCategories = useMemo(() => {
    const categoriesByIncidentCategory = new Map(
      incidentTypeCategories.flatMap((category) => [
        [category.id, category] as const,
        [category.label, category] as const,
      ]),
    );

    for (const incidentType of incidentTypes) {
      const incidentTypeCategoryId =
        incidentType.incidentTypeCategoryId?.trim() || "Uncategorized";
      if (!categoriesByIncidentCategory.has(incidentTypeCategoryId)) {
        categoriesByIncidentCategory.set(incidentTypeCategoryId, {
          dateCreated: null,
          dateModified: null,
          description: null,
          id: `fallback-${incidentTypeCategoryId}`,
          label: incidentTypeCategoryId,
        });
      }
    }

    return Array.from(
      new Map(
        Array.from(categoriesByIncidentCategory.values()).map((category) => [
          category.id,
          category,
        ]),
      ).values(),
    );
  }, [incidentTypeCategories, incidentTypes]);
  const typesByCategory = useMemo(() => {
    const map = new Map<string, IncidentType[]>();
    for (const category of displayCategories) {
      map.set(category.id, []);
    }
    for (const mt of incidentTypes) {
      const incidentTypeCategoryId =
        mt.incidentTypeCategoryId?.trim() || "Uncategorized";
      const category = incidentTypeCategories.find(
        (item) =>
          item.id === incidentTypeCategoryId ||
          item.label === incidentTypeCategoryId,
      );
      const key = category?.id ?? `fallback-${incidentTypeCategoryId}`;
      if (map.has(key)) {
        const list = map.get(key);
        if (list) {
          list.push(mt);
        }
      }
    }
    for (const list of map.values()) {
      list.sort((a, b) =>
        getIncidentTypeLabel(a).localeCompare(getIncidentTypeLabel(b)),
      );
    }
    return map;
  }, [displayCategories, incidentTypeCategories, incidentTypes]);

  const canCreate = hasActionPermission(
    ACTION_PERMISSION_KEYS.incidentType,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdate = hasActionPermission(
    ACTION_PERMISSION_KEYS.incidentType,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.incidentType,
    ACTION_PERMISSION_VALUES.delete,
  );
  const defaultAccordionValue: string | undefined = undefined;

  const incidentTypeToDelete = incidentTypes.find(
    (type) => type.id === incidentTypeToDeleteId,
  );

  const openEdit = (type: IncidentType) => {
    setActiveTypeId(type.id);
    setIsEditingType(true);
  };

  const handleDeleteIncidentType = async () => {
    if (!incidentTypeToDelete) return;

    await deleteIncidentType.mutateAsync({ id: incidentTypeToDelete.id });
  };

  if (isError || isIncidentTypeCategoriesError) {
    return <Text variant="subHeading">{serverErrorFallbackText}</Text>;
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="gap-y-1">
          <Text variant="title">{title}</Text>
        </div>
        {canCreate ? (
          <Button
            variant="foreground"
            size="md"
            className="shrink-0"
            onClick={() => {
              setAddingTypeCategoryId(undefined);
              setIsAddingType(true);
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {addButtonLabel}
          </Button>
        ) : null}
      </div>

      {isLoading || isIncidentTypeCategoriesLoading ? (
        <div className="flex flex-col gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="border-border/60 shadow-sm">
              <CardContent className="p-4 sm:p-6">
                <div className="bg-muted h-14 w-full animate-pulse rounded-md" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : incidentTypes.length === 0 ? (
        <Card className="border-border/60 border-dashed shadow-sm">
          <CardContent className="py-10 text-center">
            <Text variant="body">No incident types yet</Text>
            <Text variant="subHeading" className="mt-0.5">
              Add an incident type to classify cases for your firm.
            </Text>
          </CardContent>
        </Card>
      ) : (
        <div
          data-testid="matter-types-table"
          className="flex w-full flex-col gap-4"
        >
          <Accordion
            type="single"
            collapsible
            defaultValue={defaultAccordionValue}
            className="flex w-full flex-col gap-4"
          >
            {displayCategories.map((category) => {
              const typesInCategory = typesByCategory.get(category.id) ?? [];
              const count = typesInCategory.length;
              const accent =
                CATEGORY_ACCENTS[category.label] ?? DEFAULT_CATEGORY_ACCENT;

              return (
                <AccordionItem
                  key={category.id}
                  value={category.id}
                  className="border-0"
                >
                  <Card className="border-border/60 overflow-hidden shadow-sm">
                    <CardContent className="p-0">
                      <div className="group hover:bg-light-primary/50 flex w-full cursor-pointer items-center px-4 py-5 transition-colors sm:px-6">
                        <AccordionTrigger
                          className="min-w-0 flex-1 cursor-pointer items-center py-0 hover:bg-transparent hover:no-underline [&[data-state=open]_.matter-type-chevron]:rotate-90"
                          icon={<></>}
                        >
                          <div className="flex min-w-0 flex-1 items-center gap-3 pr-2 text-left">
                            <ChevronRight
                              className="matter-type-chevron text-muted-foreground h-4 w-4 shrink-0 self-center transition-transform duration-200"
                              aria-hidden
                            />
                            <div
                              className={cn(
                                "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border",
                                accent.boxClass,
                              )}
                              aria-hidden
                            >
                              <Briefcase
                                className={cn("h-4 w-4", accent.iconClass)}
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <Text variant="body">{category.label}</Text>
                              <Text variant="subHeading" className="mt-0.5">
                                {count}{" "}
                                {count === 1
                                  ? "incident type"
                                  : "incident types"}
                              </Text>
                            </div>
                          </div>
                        </AccordionTrigger>
                      </div>
                      <AccordionContent className="px-0 pb-0">
                        <div className="border-border/60 divide-border/60 divide-y border-t">
                          {typesInCategory.length === 0 ? (
                            <div className="flex flex-col items-center gap-4 px-4 py-8 text-center sm:px-6">
                              <Text variant="subHeading">
                                No incident types in this category yet.
                              </Text>
                              {canCreate ? (
                                <Button
                                  onClick={() => {
                                    setAddingTypeCategoryId(category.id);
                                    setIsAddingType(true);
                                  }}
                                >
                                  <Plus className="h-4 w-4" />
                                  Add incident type
                                </Button>
                              ) : null}
                            </div>
                          ) : (
                            typesInCategory.map((type) => (
                              <IncidentTypeContextMenu
                                key={type.id}
                                incidentType={type}
                                canEdit={canUpdate}
                                canDelete={canDelete}
                                onEdit={() => {
                                  openEdit(type);
                                }}
                                onDelete={() => {
                                  setIncidentTypeToDeleteId(type.id);
                                }}
                                trigger={
                                  <div
                                    role="button"
                                    tabIndex={0}
                                    data-testid="matter-type-row"
                                    className={cn(
                                      "group hover:bg-muted/60 flex w-full cursor-pointer items-start gap-3 px-4 py-4 text-left transition-colors sm:px-6",
                                      !canUpdate &&
                                        "cursor-default hover:bg-transparent",
                                    )}
                                    onClick={() => {
                                      if (canUpdate) {
                                        openEdit(type);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (
                                        canUpdate &&
                                        (e.key === "Enter" || e.key === " ")
                                      ) {
                                        e.preventDefault();
                                        openEdit(type);
                                      }
                                    }}
                                  >
                                    <div className="min-w-0 flex-1">
                                      <Text variant="body">
                                        {getIncidentTypeLabel(type)}
                                      </Text>
                                      {type.description ? (
                                        <Text
                                          variant="caption"
                                          className="mt-0.5"
                                        >
                                          {type.description}
                                        </Text>
                                      ) : null}
                                      <IncidentTypeModuleChips
                                        incidentType={type}
                                      />
                                    </div>
                                    {canUpdate ? (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="text-muted-foreground h-9 w-9 shrink-0 p-0 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openEdit(type);
                                        }}
                                      >
                                        <span className="sr-only">
                                          Edit {getIncidentTypeLabel(type)}
                                        </span>
                                        <Pencil
                                          className="h-4 w-4"
                                          aria-hidden
                                        />
                                      </Button>
                                    ) : null}
                                  </div>
                                }
                              />
                            ))
                          )}
                        </div>
                      </AccordionContent>
                    </CardContent>
                  </Card>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      )}

      <AddOrEditIncidentTypeModal
        editing={false}
        open={isAddingType}
        setOpen={(open) => {
          setIsAddingType(open);
          if (!open) {
            setAddingTypeCategoryId(undefined);
          }
        }}
        initialIncidentTypeCategoryId={addingTypeCategoryId}
      />
      <AddOrEditIncidentTypeModal
        activeIncidentType={
          incidentTypes.find((t) => t.id === activeTypeId) as IncidentType
        }
        isReadOnly={!canUpdate}
        editing={true}
        open={isEditingType}
        setOpen={setIsEditingType}
        setActiveIncidentTypeId={setActiveTypeId}
      />
      <ActionConfirmationModal
        open={Boolean(incidentTypeToDelete)}
        setOpen={(open) => {
          if (!open) setIncidentTypeToDeleteId(null);
        }}
        title="Delete incident type"
        description={`Are you sure you want to delete "${incidentTypeToDelete?.label}"? If this incident type is linked to any other items in settings, the link will be removed. If this incident type is connected to any matters, this deletion will be blocked.`}
        afterActionFn={handleDeleteIncidentType}
      />
    </div>
  );
}

export default function IncidentTypesSettingsView({
  title,
  addButtonLabel,
}: {
  title?: string;
  addButtonLabel?: string;
} = {}) {
  return (
    <ErrorBoundary
      fallback={<Text variant="subHeading">{serverErrorFallbackText}</Text>}
    >
      <IncidentTypesSettingsViewInner
        title={title}
        addButtonLabel={addButtonLabel}
      />
    </ErrorBoundary>
  );
}
